import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { getAccounts, getTransactions, getTransactionsCredencial, getCVUInfo, exportMovements } from '../services/accountService'
import { getTransferInfoCoelsa } from '../services/transferService'
import { format, subDays, parse } from 'date-fns'
import { es } from 'date-fns/locale'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { LuList, LuSearch, LuArrowDown, LuSheet, LuFileText, LuEye, LuX, LuLoader } from 'react-icons/lu'
import './MovementsPage.css'

// Registrar locale español para el datepicker
registerLocale('es', es)

// Función para formatear monto con puntos de miles (formato argentino)
// Permite +/- al inicio para filtrar entrantes/salientes
const formatFilterAmount = (value) => {
  if (!value) return ''
  
  // Preservar el signo si existe
  let sign = ''
  let cleanValue = value
  if (value.startsWith('+') || value.startsWith('-')) {
    sign = value[0]
    cleanValue = value.substring(1)
  }
  
  // Quitar todo excepto números y coma
  cleanValue = cleanValue.replace(/[^\d,]/g, '')
  
  // Separar parte entera y decimal
  const parts = cleanValue.split(',')
  let integerPart = parts[0] || ''
  let decimalPart = parts[1] !== undefined ? parts[1].slice(0, 2) : ''
  
  // Quitar ceros a la izquierda excepto si es solo "0"
  integerPart = integerPart.replace(/^0+/, '') || ''
  
  // Agregar puntos de miles
  if (integerPart.length > 3) {
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  }
  
  // Reconstruir el valor
  let result = integerPart
  if (parts.length > 1) {
    result = integerPart + ',' + decimalPart
  }
  
  return sign + result
}

// Función para formatear número a formato argentino para CSV (coma decimal, punto miles)
// Ej: 1234567.89 → "1.234.567,89"
const formatAmountForCSV = (value) => {
  const num = parseFloat(value) || 0
  // Usar toLocaleString con es-AR para formato argentino
  // minimumFractionDigits: 2 para siempre mostrar 2 decimales
  return num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Función para limpiar formato y obtener número (quita puntos de miles, convierte coma a punto)
const cleanFilterAmount = (value) => {
  if (!value) return { number: 0, hasSign: false, isPositive: false, isNegative: false }
  
  const hasPositiveSign = value.startsWith('+')
  const hasNegativeSign = value.startsWith('-')
  
  // Quitar signo, quitar puntos de miles, reemplazar coma por punto
  const cleaned = value.replace(/^[+-]/, '').replace(/\./g, '').replace(',', '.')
  const number = parseFloat(cleaned) || 0
  
  return { number, hasSign: hasPositiveSign || hasNegativeSign, isPositive: hasPositiveSign, isNegative: hasNegativeSign }
}

// CACHE BUST: v20260102-fix-timestamp-final
function MovementsPage() {
  const { userRole, roleReady, isPortalUser, assignedAccounts } = useAuth()
  const [accounts, setAccounts] = useState([])
  const [selectedAccount, setSelectedAccount] = useState('')
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingAccounts, setLoadingAccounts] = useState(true)
  const [error, setError] = useState('')
  const [searchPerformed, setSearchPerformed] = useState(false)
  
  // Filtros - Usamos Date objects para el DatePicker
  const [fromDate, setFromDate] = useState(subDays(new Date(), 7))
  const [toDate, setToDate] = useState(new Date())
  const [limit, setLimit] = useState(50)
  const [offset, setOffset] = useState(1)
  
  // Filtros adicionales para CREDENCIAL
  const [accountNumber, setAccountNumber] = useState('')
  const [page, setPage] = useState(1)
  const [totalMovements, setTotalMovements] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  
  // Filtros de búsqueda en resultados (CREDENCIAL)
  const [filterAmount, setFilterAmount] = useState('')
  const [filterDescription, setFilterDescription] = useState('')
  
  // Filtros avanzados para usuarios con DB local
  const [filterOrigin, setFilterOrigin] = useState('')  // Titular de origen
  const [filterCvuOrigen, setFilterCvuOrigen] = useState('')  // CVU de origen
  
  // Búsqueda global en todas las páginas
  const [isSearchingAll, setIsSearchingAll] = useState(false)
  const [searchAllProgress, setSearchAllProgress] = useState({ page: 0, found: 0, total: 0 })
  const [globalSearchResults, setGlobalSearchResults] = useState(null) // null = no búsqueda global activa
  const [searchCancelled, setSearchCancelled] = useState(false)
  
  // Modal de detalle de movimiento (CREDENCIAL)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedMovement, setSelectedMovement] = useState(null)
  const [cvuInfo, setCvuInfo] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  
  // Info de origen para transferencias COELSA (key: authorization_code)
  const [originInfo, setOriginInfo] = useState({})
  const [loadingOrigins, setLoadingOrigins] = useState(false)
  
  // Progreso de carga (0-100)
  const [loadProgress, setLoadProgress] = useState(0)
  const [loadPhase, setLoadPhase] = useState('') // 'movements' | 'origins' | ''
  
  // Modal de origen (al hacer click en el nombre)
  const [showOriginModal, setShowOriginModal] = useState(false)
  const [selectedOrigin, setSelectedOrigin] = useState(null)
  
  // Estados para exportación masiva
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportType, setExportType] = useState('csv') // 'csv' o 'pdf'
  const [exportScope, setExportScope] = useState('current') // 'current' o 'all'
  const [isExporting, setIsExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0, movements: 0 })
  const [exportCancelled, setExportCancelled] = useState(false)
  const [showExportConfirm, setShowExportConfirm] = useState(false) // Confirmación para "todas"
  const [hasLocalDB, setHasLocalDB] = useState(false) // Usuario tiene DB local (exportación rápida)

  useEffect(() => {
    if (userRole !== 'CREDENCIAL') {
      if (isPortalUser && assignedAccounts && assignedAccounts.length > 0) {
        const mapped = assignedAccounts.map(acc => ({
          id: acc.account_id || acc.cvu,
          number: acc.cvu,
          type: '',
          account_routing: { address: acc.cvu },
          cvu: acc.cvu
        }))
        setAccounts(mapped)
        if (mapped.length > 0) setSelectedAccount(mapped[0].id)
        setLoadingAccounts(false)
      } else {
        loadAccounts()
      }
    } else {
      setLoadingAccounts(false)
    }
  }, [userRole, assignedAccounts])

  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true)
      const response = await getAccounts()
      const accountsList = response.data || []
      setAccounts(accountsList)
      if (accountsList.length > 0) {
        setSelectedAccount(accountsList[0].id)
      }
    } catch (err) {
      setError('Error al cargar cuentas')
    } finally {
      setLoadingAccounts(false)
    }
  }

  const loadTransactions = async () => {
    // Validaciones según el rol
    if (userRole === 'CREDENCIAL') {
      if (!accountNumber) {
        setError('Debe ingresar un número de cuenta')
        return
      }
      if (!fromDate || !toDate) {
        setError('Debe seleccionar fechas desde y hasta')
        return
      }
    } else {
      if (!selectedAccount) {
        setError('Debe seleccionar una cuenta')
        return
      }
      if (!fromDate || !toDate) {
        setError('Debe seleccionar fechas')
        return
      }
      if (!limit) {
        setError('Debe seleccionar un límite')
        return
      }
    }

    try {
      setLoading(true)
      setError('')
      setSearchPerformed(true)
      setLoadProgress(0)
      setLoadPhase('movements')
      
      let response
      
      if (userRole === 'CREDENCIAL') {
        console.log(`🔍 CREDENCIAL - Buscando movimientos para cuenta: ${accountNumber}`)
        console.log(`📅 Desde: ${fromDate} Hasta: ${toDate} Página: ${page}`)
        
        // Simular progreso mientras carga (0% a 30%)
        const progressInterval = setInterval(() => {
          setLoadProgress(prev => {
            if (prev >= 28) return prev
            return prev + 2
          })
        }, 100)
        
        // Usar POST específico para CREDENCIAL
        response = await getTransactionsCredencial({
          account: accountNumber,
          from: format(fromDate, 'yyyy-MM-dd'),
          to: format(toDate, 'yyyy-MM-dd'),
          page: page,
          filter_origin: filterOrigin || undefined,
          filter_cvu_origen: filterCvuOrigen || undefined,
          filter_amount: filterAmount || undefined
        })
        
        clearInterval(progressInterval)
        setLoadProgress(30)
        
        // Para CREDENCIAL, los datos vienen directamente
        const movimientos = response.data || []
        setTransactions(movimientos)
        
        // Debug: ver qué devuelve el API
        console.log('📦 Respuesta completa:', response)
        console.log('📊 Pagination:', response.pagination)
        console.log('📋 FullResponse:', response.fullResponse)
        console.log('🏷️ Source:', response.source)
        
        // Guardar info de paginación
        // El total puede venir de pagination.total o de fullResponse.total
        const total = response.pagination?.total || response.fullResponse?.total || movimientos.length
        setTotalMovements(total)
        const pages = Math.ceil(total / 20) // 20 movimientos por página
        setTotalPages(pages > 0 ? pages : 1)
        
        console.log(`✅ ${movimientos.length} movimientos de CREDENCIAL (página ${page} de ${pages}, total: ${total})`)
        
        // DEBUG: Ver qué campos tiene la respuesta
        console.log('🔎 DEBUG response.source:', response.source)
        console.log('🔎 DEBUG response.preloadedOriginInfo:', response.preloadedOriginInfo ? Object.keys(response.preloadedOriginInfo).length : 'UNDEFINED')
        console.log('🔎 DEBUG condición LOCAL_DB:', response.source === 'LOCAL_DB' && response.preloadedOriginInfo)
        
        // Si viene de LOCAL_DB, usar la info de origen pre-cargada
        if (response.source === 'LOCAL_DB' && response.preloadedOriginInfo) {
          console.log('⚡ Usando info de origen pre-cargada desde DB local')
          console.log('⚡ Keys pre-cargadas:', Object.keys(response.preloadedOriginInfo))
          setOriginInfo(prev => ({ ...prev, ...response.preloadedOriginInfo }))
          setHasLocalDB(true) // Usuario tiene DB local - exportación rápida disponible
          setLoadProgress(100)
          setLoadPhase('')
        }
        // Si NO viene de LOCAL_DB, cargar info de origen desde Credencial
        else if (movimientos.length > 0) {
          console.log('🌐 NO es LOCAL_DB, llamando a Credencial para origenes...')
          setHasLocalDB(false) // Usuario NO tiene DB local - exportación tradicional
          loadOriginInfo(movimientos)
        } else {
          setHasLocalDB(false) // Usuario NO tiene DB local
          setLoadProgress(100)
          setLoadPhase('')
        }
        
      } else {
        // BIND - Lógica original
        console.log(`🔍 BIND - Buscando transacciones para cuenta: ${selectedAccount}`)
        console.log(`📅 Desde: ${fromDate} Hasta: ${toDate} Límite: ${limit}`)
        
        response = await getTransactions(selectedAccount, {
          from: format(fromDate, 'yyyy-MM-dd'),
          to: format(toDate, 'yyyy-MM-dd'),
          limit,
          offset,
          sort: 'DESC'
        })
        
        // Filtrar transacciones por fecha en el frontend
        let filteredTransactions = response.data || []
        
        if (fromDate || toDate) {
          const fromDateStr = fromDate ? format(fromDate, 'yyyy-MM-dd') : null
          const toDateStr = toDate ? format(toDate, 'yyyy-MM-dd') : null
          filteredTransactions = filteredTransactions.filter(txn => {
            const txnDate = new Date(txn.details.posted).toISOString().split('T')[0]
            if (fromDateStr && txnDate < fromDateStr) return false
            if (toDateStr && txnDate > toDateStr) return false
            return true
          })
        }
        
        // Aplicar límite en frontend
        if (limit) {
          filteredTransactions = filteredTransactions.slice(0, limit)
        }
        
        setTransactions(filteredTransactions)
        console.log(`✅ ${response.data?.length || 0} transacciones de BIND`)
        console.log(`🔍 ${filteredTransactions.length} después de filtrar`)
      }
      
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error al cargar movimientos'
      const httpCode = err.response?.data?.httpCode || err.response?.status || 500
      setError(`HTTP ${httpCode} - ${errorMsg}`)
      setTransactions([])
    } finally {
      setLoading(false)
    }
  }

  // Búsqueda inicial (resetea a página 1)
  const handleSearch = () => {
    setPage(1)
    setTotalMovements(0)
    setTotalPages(1)
    loadTransactions()
  }

  // Navegación de páginas para CREDENCIAL
  const goToPreviousPage = () => {
    if (page > 1) {
      const newPage = page - 1
      setPage(newPage)
      loadTransactionsForPage(newPage)
    }
  }

  const goToNextPage = () => {
    // Hay más páginas si la actual tiene 20 movimientos
    if (transactions.length === 20) {
      const newPage = page + 1
      setPage(newPage)
      loadTransactionsForPage(newPage)
    }
  }

  // Función separada para cargar por página (evita resetear página a 1)
  const loadTransactionsForPage = async (pageNumber) => {
    if (!accountNumber || !fromDate || !toDate) return
    
    try {
      setLoading(true)
      setError('')
      setLoadProgress(0)
      setLoadPhase('movements')
      
      console.log(`📄 Cargando página ${pageNumber} para cuenta ${accountNumber}`)
      
      // Simular progreso mientras carga (0% a 30%)
      const progressInterval = setInterval(() => {
        setLoadProgress(prev => {
          if (prev >= 28) return prev
          return prev + 2
        })
      }, 100)
      
      const response = await getTransactionsCredencial({
        account: accountNumber,
        from: format(fromDate, 'yyyy-MM-dd'),
        to: format(toDate, 'yyyy-MM-dd'),
        page: pageNumber,
        filter_origin: filterOrigin || undefined,
        filter_cvu_origen: filterCvuOrigen || undefined,
        filter_amount: filterAmount || undefined
      })
      
      clearInterval(progressInterval)
      setLoadProgress(30)
      
      const movimientos = response.data || []
      setTransactions(movimientos)
      
      const total = response.pagination?.total || movimientos.length
      setTotalMovements(total)
      const pages = Math.ceil(total / 20)
      setTotalPages(pages > 0 ? pages : 1)
      
      // Si viene de LOCAL_DB, usar la info de origen pre-cargada
      if (response.source === 'LOCAL_DB' && response.preloadedOriginInfo) {
        console.log('⚡ Usando info de origen pre-cargada desde DB local (página)')
        setOriginInfo(prev => ({ ...prev, ...response.preloadedOriginInfo }))
        setLoadProgress(100)
        setLoadPhase('')
      }
      // Si NO viene de LOCAL_DB, cargar info de origen desde Credencial
      else if (movimientos.length > 0) {
        loadOriginInfo(movimientos)
      } else {
        setLoadProgress(100)
        setLoadPhase('')
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error al cargar movimientos'
      setError(errorMsg)
      setLoadProgress(0)
      setLoadPhase('')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm')
  }

  // Formatear timestamp - Solo reformatea de YYYY-MM-DD HH:MM:SS a DD/MM/YYYY HH:MM
  // La DB ya guarda en hora Argentina (UTC-3), no se necesitan conversiones
  const formatTimestamp = (timestamp) => {
    if (!timestamp || timestamp === '-') return '-'
    
    // Extraer YYYY-MM-DD HH:MM:SS directamente con regex
    const match = timestamp.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/)
    if (match) {
      const [, year, month, day, hour, minute] = match
      return `${day}/${month}/${year} ${hour}:${minute}`
    }
    
    // Fallback: devolver tal cual
    return timestamp
  }

  // Parsear CVU del merchant_name (formato: "Transf 0000003100025596003275")
  // Acepta CVUs de 21 o 22 dígitos
  const parseCvuFromMerchantName = (merchantName) => {
    if (!merchantName) return null
    const match = merchantName.match(/Transf\s+(\d{21,22})/)
    return match ? match[1] : null
  }

  // Determinar si es COMISION COELSA (no tiene info de origen)
  const isCoelsaComision = (txn) => {
    return txn.merchant_name?.toUpperCase().includes('COMISION COELSA')
  }

  // Determinar si es transferencia COELSA (por merchant_id o merchant_name)
  // EXCLUYE comisiones COELSA
  const isCoelsaTransfer = (txn) => {
    // Si es comisión COELSA, no es una transferencia con info de origen
    if (isCoelsaComision(txn)) return false
    
    // Es COELSA si merchant_id es "COELSA" O merchant_name empieza con "Transf " seguido de números
    const byMerchantId = txn.merchant_id === 'COELSA'
    const byMerchantName = txn.merchant_name?.startsWith('Transf ') && /Transf \d+/.test(txn.merchant_name)
    return byMerchantId || byMerchantName
  }

  // Preparar timestamp para enviar al endpoint de Credencial
  // NOTA: La DB ya guarda timestamps en hora Argentina (UTC-3)
  // Solo normalizamos el formato, NO convertimos timezone
  const prepareTimestampForEndpoint = (timestamp) => {
    if (!timestamp) return timestamp
    // El timestamp ya viene en hora Argentina desde la DB
    // Solo aseguramos formato "YYYY-MM-DD HH:MM:SS"
      return timestamp
  }

  // Cargar info de origen para movimientos COELSA
  const loadOriginInfo = async (movimientos) => {
    console.log('📋 Total movimientos recibidos:', movimientos.length)
    
    // Filtrar solo los que son COELSA (por merchant_id o merchant_name)
    const coelsaMovements = movimientos.filter(isCoelsaTransfer)
    
    console.log('💰 Movimientos COELSA encontrados:', coelsaMovements.length)
    
    if (coelsaMovements.length === 0) {
      console.log('ℹ️ No hay movimientos COELSA para consultar origen')
      setLoadProgress(100)
      setLoadPhase('')
      return
    }
    
    console.log(`🔍 Consultando origen de ${coelsaMovements.length} transferencias COELSA...`)
    setLoadingOrigins(true)
    setLoadPhase('origins')
    
    const newOriginInfo = { ...originInfo }
    const total = coelsaMovements.length
    let processed = 0
    
    for (const txn of coelsaMovements) {
      const authCode = txn.autorization_code
      const montoPositivo = Math.abs(parseFloat(txn.amount)).toString()
      
      // Key única: auth_code + monto (para diferenciar transferencia de comisión con mismo auth_code)
      const uniqueKey = `${authCode}_${montoPositivo}`
      
      // Saltar si ya tenemos la info
      if (newOriginInfo[uniqueKey]) {
        processed++
        // Actualizar progreso (30% a 100%)
        const originProgress = 30 + (processed / total) * 70
        setLoadProgress(Math.round(originProgress))
        continue
      }
      
      try {
        // IMPORTANTE: Usar pos_date y pos_time, NO timestamp
        // Según especificación de Credencial (mail de Federico)
        // pos_date: "20251231", pos_time: "124236"
        
        if (!txn.pos_date || !txn.pos_time) {
          console.log(`⚠️ Movimiento ${uniqueKey} sin pos_date/pos_time, saltando consulta`)
          newOriginInfo[uniqueKey] = { nombre: '-', error: false, reason: 'NO_POS_DATA' }
          processed++
          const originProgress = 30 + (processed / total) * 70
          setLoadProgress(Math.round(originProgress))
          continue
        }
        
        const result = await getTransferInfoCoelsa(
          txn.pos_date,
          txn.pos_time,
          authCode,
          montoPositivo
        )
        
        if (result.success && result.data?.from) {
          newOriginInfo[uniqueKey] = {
            nombre: result.data.from.card_holder_name || '-',
            cvu: result.data.from.cvu || '-',
            cuit: result.data.from.persona_tax_id || '-',
            // También guardar info del destino
            destinoNombre: result.data.to?.card_holder_name || '-',
            destinoCvu: result.data.to?.cvu || '-',
            destinoCuit: result.data.to?.persona_tax_id || '-',
            // Código COELSA
            coelsaId: result.data.coelsa_id || '-'
          }
          console.log(`✅ Origen de ${uniqueKey}: ${result.data.from.card_holder_name}, Destino: ${result.data.to?.card_holder_name}, COELSA: ${result.data.coelsa_id}`)
        } else {
          // FALLBACK: Si getTransferInfo falla, intentar con getCVUInfo usando el CVU del merchant_name
          // IMPORTANTE: No hacer fallback para comisiones COELSA (siempre deben mostrar "-")
          if (isCoelsaComision(txn)) {
            console.log(`⏭️ Saltando fallback para comisión COELSA: ${uniqueKey}`)
            newOriginInfo[uniqueKey] = { nombre: '-', error: false }
          } else {
            console.log(`⚠️ getTransferInfo sin contenido para ${uniqueKey}, intentando fallback con getCVUInfo...`)
            const cvuOrigen = parseCvuFromMerchantName(txn.merchant_name)
            
            if (cvuOrigen) {
              try {
                const cvuResult = await getCVUInfo(cvuOrigen)
                // Los datos pueden estar en data o en fullResponse
                const data = cvuResult.data || cvuResult.fullResponse
                if (cvuResult.success && data) {
                  newOriginInfo[uniqueKey] = {
                    nombre: data.nombre || data.name || '-',
                    cvu: cvuOrigen,
                    cuit: data.cuit || data.tax_id || '-'
                  }
                  console.log(`✅ Fallback exitoso para ${uniqueKey}: ${data.nombre || data.name}`)
                } else {
                  newOriginInfo[uniqueKey] = { nombre: '-', error: true }
                }
              } catch (fallbackErr) {
                console.error(`❌ Fallback también falló para ${uniqueKey}:`, fallbackErr.message)
                newOriginInfo[uniqueKey] = { nombre: '-', error: true }
              }
            } else {
              newOriginInfo[uniqueKey] = { nombre: '-', error: true }
            }
          }
        }
      } catch (err) {
        console.error(`❌ Error consultando origen de ${uniqueKey}:`, err.message)
        newOriginInfo[uniqueKey] = { nombre: '-', error: true }
      }
      
      processed++
      // Actualizar progreso (30% a 100%)
      const originProgress = 30 + (processed / total) * 70
      setLoadProgress(Math.round(originProgress))
    }
    
    setOriginInfo(newOriginInfo)
    setLoadingOrigins(false)
    setLoadProgress(100)
    setLoadPhase('')
  }

  // Buscar en TODAS las páginas (búsqueda global por importe)
  const searchInAllPages = async () => {
    if (!filterAmount || !accountNumber || !fromDate || !toDate) return
    
    setIsSearchingAll(true)
    setSearchCancelled(false)
    setGlobalSearchResults(null)
    setSearchAllProgress({ page: 0, found: 0, total: 0 })
    
    // Usar la función de limpieza que maneja formato argentino (puntos miles, coma decimal)
    const { number: amountFilter, isPositive: hasPositiveSign, isNegative: hasNegativeSign } = cleanFilterAmount(filterAmount)
    
    let allMatchingResults = []
    let currentPage = 1
    let hasMore = true
    let totalScanned = 0
    
    try {
      while (hasMore && !searchCancelled) {
        setSearchAllProgress({ 
          page: currentPage, 
          found: allMatchingResults.length,
          total: totalScanned 
        })
        
        const response = await getTransactionsCredencial({
          account: accountNumber,
          from: fromDate,
          to: toDate,
          page: currentPage
        })
        
        const movimientos = response.data || []
        totalScanned += movimientos.length
        
        // Filtrar por importe (con soporte para +/- y formato argentino)
        const matches = movimientos.filter(txn => {
          const txnAmount = parseFloat(txn.amount) || 0
          const absAmount = Math.abs(txnAmount)
          const amountMatches = Math.abs(absAmount - amountFilter) < 0.01 || absAmount === amountFilter
          
          if (!amountMatches) return false
          if (hasPositiveSign) return txnAmount > 0
          if (hasNegativeSign) return txnAmount < 0
          return true
        })
        
        allMatchingResults = [...allMatchingResults, ...matches]
        
        // Si devuelve menos de 20, es la última página
        if (movimientos.length < 20) {
          hasMore = false
        } else {
          currentPage++
        }
        
        // Pequeña pausa para no saturar la API
        await new Promise(resolve => setTimeout(resolve, 150))
      }
      
      if (!searchCancelled) {
        setGlobalSearchResults(allMatchingResults)
        setSearchAllProgress({ 
          page: currentPage, 
          found: allMatchingResults.length,
          total: totalScanned 
        })
        
        // Cargar info de origen para los resultados encontrados
        if (allMatchingResults.length > 0) {
          loadOriginInfo(allMatchingResults)
        }
      }
    } catch (err) {
      console.error('Error en búsqueda global:', err)
      setGlobalSearchResults([])
    } finally {
      setIsSearchingAll(false)
    }
  }
  
  // Cancelar búsqueda global
  const cancelGlobalSearch = () => {
    setSearchCancelled(true)
    setIsSearchingAll(false)
  }
  
  // Limpiar resultados de búsqueda global
  const clearGlobalSearch = () => {
    setGlobalSearchResults(null)
    setFilterAmount('')
  }

  // Abrir modal de detalle
  const handleViewDetail = async (txn) => {
    setSelectedMovement(txn)
    setCvuInfo(null)
    setDetailLoading(true)

    // Si es COELSA, obtener info del CVU antes de mostrar el modal
    if (isCoelsaTransfer(txn)) {
      const cvu = parseCvuFromMerchantName(txn.merchant_name)
      if (cvu) {
        try {
          const response = await getCVUInfo(cvu)
          if (response.success) {
            setCvuInfo(response.data || response.fullResponse)
          }
        } catch (err) {
          console.error('Error consultando CVU info:', err)
        }
      }
    }

    setDetailLoading(false)
    setShowDetailModal(true)
  }

  // Cerrar modal de detalle
  const handleCloseDetailModal = () => {
    setShowDetailModal(false)
    setSelectedMovement(null)
    setCvuInfo(null)
  }

  // Descargar comprobante del movimiento
  const handleDownloadComprobante = () => {
    if (!selectedMovement) return
    
    const isCredit = parseFloat(selectedMovement.amount) >= 0
    const tipoMovimiento = isCredit ? 'Transferencia Recibida' : 'Transferencia Enviada'
    
    // Obtener info completa desde originInfo
    const authCode = selectedMovement.autorization_code
    const montoPositivo = Math.abs(parseFloat(selectedMovement.amount) || 0).toString()
    const uniqueKey = `${authCode}_${montoPositivo}`
    const transferData = originInfo[uniqueKey] || {}
    const coelsaId = transferData.coelsaId || '-'
    
    const printWindow = window.open('', '_blank')
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Comprobante de Transferencia</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Arial, sans-serif; 
            padding: 40px;
            background: #f5f5f5;
          }
          .comprobante {
            max-width: 500px;
            margin: 0 auto;
            background: white;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            overflow: hidden;
          }
          .header {
            background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
            color: white;
            padding: 30px;
            text-align: center;
          }
          .header h1 { font-size: 24px; margin-bottom: 5px; }
          .header p { opacity: 0.8; font-size: 14px; }
          .content { padding: 30px; }
          .monto {
            text-align: center;
            padding: 20px 0;
            border-bottom: 1px solid #eee;
            margin-bottom: 20px;
          }
          .monto-valor {
            font-size: 36px;
            font-weight: 700;
            color: ${isCredit ? '#22c55e' : '#ef4444'};
          }
          .monto-tipo {
            font-size: 14px;
            color: #666;
            margin-top: 5px;
          }
          .detalle { margin-bottom: 15px; }
          .detalle-label {
            font-size: 12px;
            color: #888;
            text-transform: uppercase;
            margin-bottom: 4px;
          }
          .detalle-valor {
            font-size: 15px;
            color: #333;
            word-break: break-all;
          }
          .footer {
            background: #f8f9fa;
            padding: 20px 30px;
            text-align: center;
            font-size: 12px;
            color: #888;
          }
          .footer p { margin-bottom: 5px; }
          @media print {
            body { background: white; padding: 0; }
            .comprobante { box-shadow: none; }
          }
          @page {
            margin: 20mm;
            size: auto;
          }
        </style>
      </head>
      <body>
        <div class="comprobante">
          <div class="header">
            <p>Comprobante de Transferencia</p>
          </div>
          <div class="content">
            <div class="monto">
              <div class="monto-valor">${formatCurrency(Math.abs(parseFloat(selectedMovement.amount) || 0))}</div>
              <div class="monto-tipo">${tipoMovimiento}</div>
            </div>
            
            <div class="detalle">
              <div class="detalle-label">Fecha y Hora</div>
              <div class="detalle-valor">${formatTimestamp(selectedMovement.timestamp)}</div>
            </div>
            
            ${cvuInfo ? `
              <div class="detalle">
                <div class="detalle-label">${isCredit ? 'Remitente' : 'Destinatario'}</div>
                <div class="detalle-valor">${cvuInfo.nombre || '-'}</div>
              </div>
              
              <div class="detalle">
                <div class="detalle-label">CUIT</div>
                <div class="detalle-valor">${cvuInfo.cuit || '-'}</div>
              </div>
              
              ${cvuInfo.alias ? `
                <div class="detalle">
                  <div class="detalle-label">Alias</div>
                  <div class="detalle-valor">${cvuInfo.alias}</div>
                </div>
              ` : ''}
            ` : ''}
            
            ${coelsaId && coelsaId !== '-' ? `
            <div class="detalle">
              <div class="detalle-label">Código COELSA</div>
              <div class="detalle-valor">${coelsaId}</div>
            </div>
            ` : ''}
          </div>
          <div class="footer">
            <p>Documento generado el ${new Date().toLocaleString('es-AR')}</p>
            <p>Este comprobante es válido como constancia de la operación</p>
          </div>
        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `)
    printWindow.document.close()
  }

  // Abrir modal de origen (al hacer click en el nombre)
  // Recibe la key compuesta (authCode_monto)
  const handleOpenOriginModal = (uniqueKey) => {
    const info = originInfo[uniqueKey]
    if (info && info.nombre !== '-' && !info.error) {
      setSelectedOrigin(info)
      setShowOriginModal(true)
    }
  }

  // Cerrar modal de origen
  const handleCloseOriginModal = () => {
    setShowOriginModal(false)
    setSelectedOrigin(null)
  }

  // Confirmar exportación
  const handleExportConfirm = () => {
    // Si tiene DB local, exportar TODO directamente (sin confirmación adicional)
    if (hasLocalDB) {
      setShowExportModal(false)
      exportAllPages() // Exportación rápida desde DB local
      return
    }
    
    // Usuarios SIN DB local - comportamiento original
    if (exportScope === 'current') {
      // Exportar página actual
      if (exportType === 'csv') {
        generateCSV(transactions)
      } else {
        generatePDF(transactions)
      }
      setShowExportModal(false)
    } else {
      // Exportar todas - mostrar confirmación adicional
      setShowExportConfirm(true)
    }
  }

  // Iniciar exportación de todas las páginas
  const startExportAll = () => {
    setExportCancelled(false)
    setShowExportConfirm(false)
    exportAllPages()
  }

  // Cancelar exportación
  const cancelExport = () => {
    setExportCancelled(true)
    setIsExporting(false)
    setShowExportModal(false)
  }

  // Exportar TODAS las páginas
  // OPTIMIZACIÓN: Primero intenta exportación rápida desde DB local
  // Si falla (usuario sin DB), hace fallback al método tradicional paginado
  const exportAllPages = async () => {
    setIsExporting(true)
    setExportProgress({ current: 0, total: '?', movements: 0 })
    
    // =====================================================
    // INTENTO 1: Exportación rápida desde DB local
    // =====================================================
    try {
      console.log('📤 Intentando exportación rápida desde DB local...')
      setExportProgress({ current: 1, total: 1, movements: 0, mode: 'fast' })
      
      const response = await exportMovements({
        account: accountNumber,
        from: fromDate,
        to: toDate
      })
      
      if (response.success && response.data) {
        const allMovements = response.data
        console.log(`✅ Exportación rápida exitosa: ${allMovements.length} movimientos en ${response.exportTime}ms`)
        
        // Guardar la info de origen para usar en generateCSV/generatePDF
        if (response.preloadedOriginInfo) {
          setOriginInfo(prev => ({ ...prev, ...response.preloadedOriginInfo }))
        }
        
        setExportProgress({ current: 1, total: 1, movements: allMovements.length, mode: 'fast' })
        
        // Generar archivo según tipo
        if (exportType === 'csv') {
          generateCSV(allMovements, response.preloadedOriginInfo)
        } else {
          generatePDF(allMovements, response.preloadedOriginInfo)
        }
        
        setIsExporting(false)
        setShowExportModal(false)
        return // Éxito - no hacer fallback
      }
    } catch (err) {
      // Si el error es NO_LOCAL_DB, hacer fallback silencioso
      if (err.response?.data?.code === 'NO_LOCAL_DB') {
        console.log('⚠️ Usuario sin DB local, usando método tradicional...')
      } else {
        console.error('❌ Error en exportación rápida, haciendo fallback:', err)
      }
    }
    
    // =====================================================
    // FALLBACK: Método tradicional paginado (para usuarios sin DB)
    // =====================================================
    console.log('📄 Usando exportación tradicional paginada...')
    let allMovements = []
    let currentPage = 1
    let hasMore = true
    
    while (hasMore) {
      // Verificar si se canceló
      if (exportCancelled) {
        setIsExporting(false)
        return
      }
      
      setExportProgress({ current: currentPage, total: '?', movements: allMovements.length, mode: 'paginated' })
      
      try {
        const response = await getTransactionsCredencial({
          account: accountNumber,
          from: fromDate,
          to: toDate,
          page: currentPage
        })
        
        const movimientos = response.data || []
        allMovements = [...allMovements, ...movimientos]
        
        // Si devuelve menos de 20, es la última página
        if (movimientos.length < 20) {
          hasMore = false
        } else {
          currentPage++
        }
        
      } catch (err) {
        console.error(`Error en página ${currentPage}:`, err)
        hasMore = false // Parar si hay error
      }
      
      // Pequeña pausa para no saturar
      await new Promise(resolve => setTimeout(resolve, 100))
    }
    
    // Verificar cancelación antes de generar archivo
    if (exportCancelled) {
      setIsExporting(false)
      return
    }
    
    setExportProgress({ current: currentPage, total: currentPage, movements: allMovements.length, mode: 'paginated' })
    
    // Generar archivo según tipo
    if (exportType === 'csv') {
      generateCSV(allMovements)
    } else {
      generatePDF(allMovements)
    }
    
    setIsExporting(false)
    setShowExportModal(false)
  }

  // Generar CSV con movimientos dados
  // exportOriginInfo es opcional - si viene de exportación rápida, usarlo; si no, usar originInfo del estado
  const generateCSV = (movimientos, exportOriginInfo = null) => {
    const infoSource = exportOriginInfo || originInfo
    
    // Verificar si los movimientos tienen datos enriquecidos (DB local)
    const hasEnrichedData = movimientos.length > 0 && movimientos[0].hasEnrichedData
    
    // Header diferente según si tiene datos enriquecidos
    let csvContent = hasEnrichedData 
      ? 'Fecha,Descripción,Origen,Destino,Cuenta Origen,COELSA ID,Monto,Saldo\n'
      : 'Fecha,Descripción,Origen,Destino,Monto,Saldo\n'
    
    movimientos.forEach(txn => {
      const fecha = formatTimestamp(txn.timestamp)
      const descripcion = (txn.merchant_name || txn.tx_description || '-').replace(/,/g, ' ')
      // Formatear montos al estilo argentino (coma decimal, punto miles)
      const monto = formatAmountForCSV(txn.amount)
      const saldo = formatAmountForCSV(txn.balance_after_tx)
      
      // Obtener info de origen/destino
      const authCode = txn.autorization_code
      const montoPositivo = Math.abs(parseFloat(txn.amount) || 0).toString()
      const uniqueKey = `${authCode}_${montoPositivo}`
      const transferInfo = isCoelsaTransfer(txn) ? infoSource[uniqueKey] : null
      
      // Origen y Destino - usar datos enriquecidos si están disponibles
      const origen = txn._enriched_origin || transferInfo?.nombre || '-'
      const destino = txn._enriched_destination || transferInfo?.destinoNombre || '-'
      
      if (hasEnrichedData) {
        // Con datos enriquecidos: incluir cuenta origen y COELSA ID
        const cuentaOrigen = txn._account_number || '-'
        const coelsaId = txn._enriched_coelsa_id || transferInfo?.coelsaId || '-'
        csvContent += `${fecha},"${descripcion}","${origen}","${destino}","${cuentaOrigen}","${coelsaId}","${monto}","${saldo}"\n`
      } else {
        // Sin datos enriquecidos: formato original
        csvContent += `${fecha},"${descripcion}","${origen}","${destino}","${monto}","${saldo}"\n`
      }
    })
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `movimientos_${accountNumber}_${format(fromDate, 'yyyy-MM-dd')}_${format(toDate, 'yyyy-MM-dd')}.csv`
    link.click()
  }

  // Generar PDF con movimientos dados
  // exportOriginInfo es opcional - si viene de exportación rápida, usarlo; si no, usar originInfo del estado
  const generatePDF = (movimientos, exportOriginInfo = null) => {
    const infoSource = exportOriginInfo || originInfo
    const printWindow = window.open('', '_blank')
    
    // Verificar si los movimientos tienen datos enriquecidos (DB local)
    const hasEnrichedData = movimientos.length > 0 && movimientos[0].hasEnrichedData
    
    // Función para obtener origen/destino de un movimiento
    const getOrigenDestino = (txn) => {
      const authCode = txn.autorization_code
      const montoPositivo = Math.abs(parseFloat(txn.amount) || 0).toString()
      const uniqueKey = `${authCode}_${montoPositivo}`
      const transferInfo = isCoelsaTransfer(txn) ? infoSource[uniqueKey] : null
      
      return {
        origen: txn._enriched_origin || transferInfo?.nombre || '-',
        destino: txn._enriched_destination || transferInfo?.destinoNombre || '-',
        cuentaOrigen: txn._account_number || '-',
        coelsaId: txn._enriched_coelsa_id || transferInfo?.coelsaId || '-'
      }
    }
    
    // Headers diferentes según si tiene datos enriquecidos
    const tableHeaders = hasEnrichedData 
      ? '<th>Fecha</th><th>Descripción</th><th>Origen</th><th>Destino</th><th>Cuenta Origen</th><th>COELSA ID</th><th style="text-align:right;">Monto</th><th style="text-align:right;">Saldo</th>'
      : '<th>Fecha</th><th>Descripción</th><th>Origen</th><th>Destino</th><th style="text-align:right;">Monto</th><th style="text-align:right;">Saldo</th>'
    
    const tableHTML = `
      <table border="1" cellpadding="8" cellspacing="0" style="width:100%; border-collapse: collapse; font-size: 11px;">
        <thead style="background: #1a1a2e; color: white;">
          <tr>
            ${tableHeaders}
          </tr>
        </thead>
        <tbody>
          ${movimientos.map(txn => {
            const { origen, destino, cuentaOrigen, coelsaId } = getOrigenDestino(txn)
            const fechaFormatted = txn.timestamp ? (() => { const m = txn.timestamp.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/); return m ? m[3]+'/'+m[2]+'/'+m[1]+' '+m[4]+':'+m[5] : txn.timestamp; })() : '-'
            const montoFormatted = parseFloat(txn.amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })
            const saldoFormatted = parseFloat(txn.balance_after_tx || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })
            const montoColor = parseFloat(txn.amount) >= 0 ? 'green' : 'red'
            
            if (hasEnrichedData) {
            return `
            <tr>
                <td>${fechaFormatted}</td>
              <td>${txn.merchant_name || txn.tx_description || '-'}</td>
              <td>${origen}</td>
              <td>${destino}</td>
                <td>${cuentaOrigen}</td>
                <td style="font-size:10px;">${coelsaId}</td>
                <td style="text-align:right; color: ${montoColor}">$${montoFormatted}</td>
                <td style="text-align:right;">$${saldoFormatted}</td>
            </tr>
              `
            } else {
              return `
              <tr>
                <td>${fechaFormatted}</td>
                <td>${txn.merchant_name || txn.tx_description || '-'}</td>
                <td>${origen}</td>
                <td>${destino}</td>
                <td style="text-align:right; color: ${montoColor}">$${montoFormatted}</td>
                <td style="text-align:right;">$${saldoFormatted}</td>
              </tr>
              `
            }
          }).join('')}
        </tbody>
      </table>
    `
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Movimientos - Finpro</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a1a2e; }
          .info { margin-bottom: 20px; color: #666; }
        </style>
      </head>
      <body>
        <h1>Reporte de Movimientos</h1>
        <div class="info">
          <p>Cuenta: ${accountNumber}</p>
          <p>Período: ${format(fromDate, 'dd/MM/yyyy')} al ${format(toDate, 'dd/MM/yyyy')}</p>
          <p>Total de movimientos: ${movimientos.length}</p>
          <p>Generado: ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
        </div>
        ${tableHTML}
      </body>
      </html>
    `)
    
    printWindow.document.close()
    printWindow.print()
  }

  // Exportar página actual a CSV
  const exportToCSV = () => {
    let csvContent = ''
    let filename = `movimientos_${format(new Date(), 'yyyy-MM-dd')}.csv`
    
    if (userRole === 'CREDENCIAL') {
      csvContent = 'Fecha,Descripción,Monto,Saldo\n'
      transactions.forEach(txn => {
        const fecha = formatTimestamp(txn.timestamp)
        const descripcion = (txn.merchant_name || txn.tx_description || '-').replace(/,/g, ' ')
        // Formatear montos al estilo argentino (coma decimal, punto miles)
        const monto = formatAmountForCSV(txn.amount)
        const saldo = formatAmountForCSV(txn.balance_after_tx)
        csvContent += `${fecha},"${descripcion}","${monto}","${saldo}"\n`
      })
    } else {
      csvContent = 'Fecha,Tipo,Contraparte,Descripción,Categoría,Monto\n'
      transactions.forEach(txn => {
        const fecha = formatDate(txn.details.posted)
        const tipo = txn.details.type
        const contraparte = (txn.counterparty?.name || '-').replace(/,/g, ' ')
        const descripcion = (txn.details.description || '-').replace(/,/g, ' ')
        const categoria = txn.metadata?.category || '-'
        // Formatear monto al estilo argentino
        const monto = formatAmountForCSV(txn.details.value.amount)
        csvContent += `${fecha},${tipo},"${contraparte}","${descripcion}",${categoria},"${monto}"\n`
      })
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename
    link.click()
  }

  // Exportar a PDF (imprimir)
  const exportToPDF = () => {
    const printWindow = window.open('', '_blank')
    
    let tableHTML = ''
    
    if (userRole === 'CREDENCIAL') {
      tableHTML = `
        <table border="1" cellpadding="8" cellspacing="0" style="width:100%; border-collapse: collapse;">
          <thead style="background: #1a1a2e; color: white;">
            <tr>
              <th>Fecha</th>
              <th>Descripción</th>
              <th style="text-align:right;">Monto</th>
              <th style="text-align:right;">Saldo</th>
            </tr>
          </thead>
          <tbody>
            ${transactions.map(txn => `
              <tr>
                <td>${txn.timestamp ? (() => { const m = txn.timestamp.match(/^(\\d{4})-(\\d{2})-(\\d{2})\\s+(\\d{2}):(\\d{2})/); return m ? m[3]+'/'+m[2]+'/'+m[1]+' '+m[4]+':'+m[5] : txn.timestamp; })() : '-'}</td>
                <td>${txn.merchant_name || txn.tx_description || '-'}</td>
                <td style="text-align:right; color: ${parseFloat(txn.amount) >= 0 ? 'green' : 'red'}">
                  $${parseFloat(txn.amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </td>
                <td style="text-align:right;">$${parseFloat(txn.balance_after_tx || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `
    } else {
      tableHTML = `
        <table border="1" cellpadding="8" cellspacing="0" style="width:100%; border-collapse: collapse;">
          <thead style="background: #1a1a2e; color: white;">
            <tr>
              <th>Fecha</th>
              <th>Tipo</th>
              <th>Contraparte</th>
              <th>Descripción</th>
              <th style="text-align:right;">Monto</th>
            </tr>
          </thead>
          <tbody>
            ${transactions.map(txn => `
              <tr>
                <td>${formatDate(txn.details.posted)}</td>
                <td>${txn.details.type === 'CREDIT' ? 'Crédito' : 'Débito'}</td>
                <td>${txn.counterparty?.name || '-'}</td>
                <td>${txn.details.description}</td>
                <td style="text-align:right; color: ${txn.details.type === 'CREDIT' ? 'green' : 'red'}">
                  $${Math.abs(txn.details.value.amount).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Movimientos - Finpro</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a1a2e; }
          .info { margin-bottom: 20px; color: #666; }
        </style>
      </head>
      <body>
        <h1>Reporte de Movimientos</h1>
        <div class="info">
          <p>Período: ${format(fromDate, 'dd/MM/yyyy')} al ${format(toDate, 'dd/MM/yyyy')}</p>
          <p>Total de movimientos: ${transactions.length}</p>
          <p>Generado: ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
        </div>
        ${tableHTML}
      </body>
      </html>
    `)
    
    printWindow.document.close()
    printWindow.print()
  }

  // Esperar a que el rol esté listo
  if (!roleReady) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando...</p>
      </div>
    )
  }

  if (loadingAccounts) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando cuentas</p>
      </div>
    )
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><LuList /> Movimientos</h1>
      </div>

      <div className="filters-card">
        <div className="filters-grid">
          {userRole === 'CREDENCIAL' && (
            /* Filtros para CREDENCIAL */
            <>
              <div className="form-group">
                <label>Número de Cuenta</label>
                {isPortalUser ? (
                  <select
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    className="form-control"
                  >
                    <option value="">Seleccione una cuenta</option>
                    {assignedAccounts.map((acc, index) => (
                      <option key={index} value={acc.account_number}>
                        {acc.account_number} - {acc.cvu}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input 
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="Ingrese número de cuenta"
                    className="form-control"
                  />
                )}
              </div>

              <div className="form-group">
                <label>Desde</label>
                <DatePicker
                  selected={fromDate}
                  onChange={(date) => setFromDate(date)}
                  dateFormat="dd/MM/yyyy"
                  locale="es"
                  className="form-control"
                  placeholderText="dd/mm/aaaa"
                  maxDate={toDate}
                />
              </div>

              <div className="form-group">
                <label>Hasta</label>
                <DatePicker
                  selected={toDate}
                  onChange={(date) => setToDate(date)}
                  dateFormat="dd/MM/yyyy"
                  locale="es"
                  className="form-control"
                  placeholderText="dd/mm/aaaa"
                  minDate={fromDate}
                  maxDate={new Date()}
                />
              </div>

              <div className="form-group">
                <label>&nbsp;</label>
                <button 
                  onClick={handleSearch}
                  className="btn-primary"
                  disabled={loading || !accountNumber}
                >
                  <LuSearch /> {loading ? 'Cargando...' : 'Buscar'}
                </button>
              </div>
            </>
          )}
          
          {userRole !== 'CREDENCIAL' && (
            /* Filtros para BIND */
            <>
              <div className="form-group">
                <label>Cuenta</label>
                <select 
                  value={selectedAccount} 
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  className="form-control"
                >
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.account_routing?.address || acc.cbu || acc.cvu || acc.number}{acc.type ? ` - ${acc.type}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Desde</label>
                <DatePicker
                  selected={fromDate}
                  onChange={(date) => setFromDate(date)}
                  dateFormat="dd/MM/yyyy"
                  locale="es"
                  className="form-control"
                  placeholderText="dd/mm/aaaa"
                  maxDate={toDate}
                />
              </div>

              <div className="form-group">
                <label>Hasta</label>
                <DatePicker
                  selected={toDate}
                  onChange={(date) => setToDate(date)}
                  dateFormat="dd/MM/yyyy"
                  locale="es"
                  className="form-control"
                  placeholderText="dd/mm/aaaa"
                  minDate={fromDate}
                  maxDate={new Date()}
                />
              </div>

              <div className="form-group">
                <label>Límite</label>
                <select 
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  className="form-control"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

              <div className="form-group">
                <label>&nbsp;</label>
                <button 
                  onClick={loadTransactions}
                  className="btn-primary"
                  disabled={loading}
                >
                  <LuSearch /> {loading ? 'Cargando...' : 'Buscar'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {(loading || loadingOrigins) && userRole === 'CREDENCIAL' && (
        <div className="loading-movements-full">
          <div className="loading-progress-bar">
            <div className="loading-progress-fill-real" style={{ width: `${loadProgress}%` }}></div>
          </div>
          <span>
            {loadPhase === 'movements' ? 'Cargando movimientos...' : 'Cargando información de origen...'}
          </span>
          <span className="progress-percent">{loadProgress}%</span>
        </div>
      )}
      
      {loading && userRole !== 'CREDENCIAL' && (
        <div className="loading-movements-full">
          <div className="loading-progress-bar">
            <div className="loading-progress-fill"></div>
          </div>
          <span>Cargando movimientos...</span>
        </div>
      )}

      {!loading && searchPerformed && transactions.length === 0 && (
        <div className="empty-state">
          <p className="empty-state-large">No se encontraron movimientos en el período seleccionado</p>
        </div>
      )}

      {/* Filtros adicionales para CREDENCIAL (solo cuando NO está cargando orígenes) */}
      {!loading && !loadingOrigins && transactions.length > 0 && userRole === 'CREDENCIAL' && !globalSearchResults && (
        <div className="filters-card filters-secondary">
          <div className="filters-grid-search">
            <div className="form-group">
              <label>Filtrar por Importe</label>
              <input 
                type="text"
                inputMode="decimal"
                value={filterAmount}
                onChange={(e) => { setFilterAmount(formatFilterAmount(e.target.value)); setGlobalSearchResults(null); }}
                placeholder="Ej: 1.500, +50.000, -100.000"
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Filtrar por Descripción</label>
              <input 
                type="text"
                value={filterDescription}
                onChange={(e) => setFilterDescription(e.target.value)}
                placeholder="Buscar en descripción..."
                className="form-control"
              />
            </div>
            {/* Filtros adicionales solo para usuarios con DB local */}
            {hasLocalDB && (
              <>
                <div className="form-group">
                  <label>Titular de origen</label>
                  <input 
                    type="text"
                    value={filterOrigin}
                    onChange={(e) => setFilterOrigin(e.target.value)}
                    placeholder="Ej: Maria, Juan..."
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>CVU de origen</label>
                  <input 
                    type="text"
                    value={filterCvuOrigen}
                    onChange={(e) => setFilterCvuOrigen(e.target.value)}
                    placeholder="Ej: 0000003100..."
                    className="form-control"
                  />
                </div>
              </>
            )}
            <div className="form-group">
              <label>&nbsp;</label>
              <button 
                onClick={() => { 
                  setFilterAmount(''); 
                  setFilterDescription(''); 
                  setFilterOrigin('');
                  setFilterCvuOrigen('');
                  setGlobalSearchResults(null); 
                }}
                className="btn-secondary"
              >
                Limpiar filtros
              </button>
            </div>
          </div>
          
          {/* Botón de búsqueda en todas las páginas - solo si hay filtro por importe y NO tiene DB local */}
          {filterAmount && !hasLocalDB && (
            <div className="global-search-hint">
              <div className="hint-message">
                <span className="hint-icon">💡</span>
                <span>El filtro por importe solo busca en la página actual ({transactions.length} movimientos)</span>
              </div>
              <button 
                onClick={searchInAllPages}
                className="btn-search-all"
                disabled={isSearchingAll}
              >
                <LuSearch /> Buscar en todas las páginas
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Modal de progreso de búsqueda global */}
      {isSearchingAll && (
        <div className="search-modal-overlay">
          <div className="search-modal">
            <div className="search-modal-header">
              <h3>🔍 Buscando importe ${filterAmount}</h3>
            </div>
            <div className="search-modal-body">
              <div className="search-progress-animation">
                <div className="search-spinner"></div>
              </div>
              <div className="search-stats">
                <div className="stat-item">
                  <span className="stat-value">{searchAllProgress.page}</span>
                  <span className="stat-label">Páginas revisadas</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{searchAllProgress.total}</span>
                  <span className="stat-label">Movimientos escaneados</span>
                </div>
                <div className="stat-item highlight">
                  <span className="stat-value">{searchAllProgress.found}</span>
                  <span className="stat-label">Coincidencias</span>
                </div>
              </div>
              <p className="search-hint">Buscando en el período {format(fromDate, 'dd/MM/yyyy')} al {format(toDate, 'dd/MM/yyyy')}...</p>
            </div>
            <div className="search-modal-footer">
              <button onClick={cancelGlobalSearch} className="btn-cancel-search">
                ✕ Cancelar búsqueda
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Resultados de búsqueda global (solo cuando TODO está cargado) */}
      {globalSearchResults && !isSearchingAll && !loadingOrigins && (
        <div className="global-search-results">
          <div className="results-header">
            <div className="results-info">
              <h3>🎯 Resultados de búsqueda: ${filterAmount}</h3>
              <p>
                Se encontraron <strong>{globalSearchResults.length}</strong> movimientos 
                en <strong>{searchAllProgress.total}</strong> movimientos revisados 
                ({searchAllProgress.page} páginas)
              </p>
            </div>
            <button onClick={clearGlobalSearch} className="btn-close-results">
              <LuX /> Volver a la vista normal
            </button>
          </div>
          
          {globalSearchResults.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Descripción</th>
                    <th>Origen</th>
                    <th className="text-right">Monto</th>
                    <th className="text-center">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {globalSearchResults.map((txn, idx) => {
                    const amount = parseFloat(txn.amount) || 0
                    const isCredit = amount > 0
                    const authCode = txn.autorization_code
                    const montoPositivo = Math.abs(amount).toString()
                    const uniqueKey = `${authCode}_${montoPositivo}`
                    const origenInfo = isCoelsaTransfer(txn) ? originInfo[uniqueKey] : null
                    const origenNombre = origenInfo?.nombre || '-'
                    const hasOriginInfo = origenInfo && origenNombre !== '-' && !origenInfo.error
                    
                    return (
                      <tr key={authCode || idx}>
                        <td>{formatTimestamp(txn.timestamp)}</td>
                        <td>{txn.merchant_name || txn.tx_description || '-'}</td>
                        <td className="origin-cell">
                          {hasOriginInfo ? (
                            <span 
                              className="origin-clickable"
                              onClick={() => handleOpenOriginModal(uniqueKey)}
                              title="Ver información del origen"
                            >
                              {origenNombre}
                            </span>
                          ) : origenNombre}
                        </td>
                        <td className={`text-right text-amount ${isCredit ? 'text-credit' : 'text-debit'}`}>
                          {formatCurrency(amount)}
                        </td>
                        <td className="text-center">
                          <button 
                            className="btn-detail"
                            onClick={() => handleViewDetail(txn)}
                            title="Ver detalle"
                          >
                            <LuEye />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results-found">
              <span className="no-results-icon">🔍</span>
              <p>No se encontraron movimientos con importe ${filterAmount}</p>
              <p className="no-results-hint">Se revisaron {searchAllProgress.total} movimientos en {searchAllProgress.page} páginas</p>
            </div>
          )}
        </div>
      )}

      {/* Tabla para CREDENCIAL (solo cuando TODO está cargado, incluyendo info de origen) */}
      {!loading && !loadingOrigins && transactions.length > 0 && userRole === 'CREDENCIAL' && !globalSearchResults && (() => {
        // Aplicar filtros
        let filteredTxns = transactions
        
        if (filterAmount) {
          // Usar la función de limpieza que maneja formato argentino (puntos miles, coma decimal)
          const { number: amountFilter, isPositive: hasPositiveSign, isNegative: hasNegativeSign } = cleanFilterAmount(filterAmount)
          
          filteredTxns = filteredTxns.filter(txn => {
            const txnAmount = parseFloat(txn.amount) || 0
            const absAmount = Math.abs(txnAmount)
            
            // Verificar si el monto coincide (exacto o con tolerancia para decimales)
            const amountMatches = Math.abs(absAmount - amountFilter) < 0.01 || absAmount === amountFilter
            
            if (!amountMatches) return false
            
            // Si tiene signo +, solo mostrar entrantes (positivos)
            if (hasPositiveSign) return txnAmount > 0
            
            // Si tiene signo -, solo mostrar salientes (negativos)
            if (hasNegativeSign) return txnAmount < 0
            
            // Sin signo, mostrar ambos
            return true
          })
        }
        
        if (filterDescription) {
          const descLower = filterDescription.toLowerCase()
          filteredTxns = filteredTxns.filter(txn => 
            (txn.merchant_name || '').toLowerCase().includes(descLower) ||
            (txn.tx_description || '').toLowerCase().includes(descLower)
          )
        }
        
        return (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Descripción</th>
                  <th>Origen</th>
                  <th className="text-right">Monto</th>
                  <th className="text-center">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {filteredTxns.map((txn, idx) => {
                  const amount = parseFloat(txn.amount) || 0
                  const isCredit = amount > 0
                  const authCode = txn.autorization_code
                  const montoPositivo = Math.abs(amount).toString()
                  const uniqueKey = `${authCode}_${montoPositivo}`
                  const origenInfo = isCoelsaTransfer(txn) ? originInfo[uniqueKey] : null
                  const origenNombre = origenInfo?.nombre || '-'
                  const hasOriginInfo = origenInfo && origenNombre !== '-' && !origenInfo.error
                  
                  return (
                    <tr key={authCode || idx}>
                      <td>{formatTimestamp(txn.timestamp)}</td>
                      <td>{txn.merchant_name || txn.tx_description || '-'}</td>
                      <td className="origin-cell">
                        {hasOriginInfo ? (
                          <span 
                            className="origin-clickable"
                            onClick={() => handleOpenOriginModal(uniqueKey)}
                            title="Ver información del origen"
                          >
                            {origenNombre}
                          </span>
                        ) : origenNombre}
                      </td>
                      <td className={`text-right text-amount ${isCredit ? 'text-credit' : 'text-debit'}`}>
                        {formatCurrency(amount)}
                      </td>
                      <td className="text-center">
                        <button 
                          className="btn-detail"
                          onClick={() => handleViewDetail(txn)}
                          title="Ver detalle"
                        >
                          <LuEye />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {filteredTxns.length === 0 && (
              <p className="filter-no-results">No hay movimientos que coincidan con los filtros</p>
            )}
          </div>
        )
      })()}

      {/* Tabla para BIND */}
      {!loading && transactions.length > 0 && userRole !== 'CREDENCIAL' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Contraparte</th>
                <th>Descripción</th>
                <th>Categoría</th>
                <th className="text-right">Monto</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((txn) => (
                <tr key={txn.id}>
                  <td>{formatDate(txn.details.posted)}</td>
                  <td>
                    <span className={`badge badge-${txn.details.type.toLowerCase()}`}>
                      {txn.details.type === 'CREDIT' ? <><LuArrowDown /> Crédito</> : <>Débito</>}
                    </span>
                  </td>
                  <td>{txn.counterparty?.name || '-'}</td>
                  <td>{txn.details.description}</td>
                  <td>{txn.metadata?.category || '-'}</td>
                  <td className={`text-right text-amount ${txn.details.type === 'CREDIT' ? 'text-credit' : 'text-debit'}`}>
                    {formatCurrency(Math.abs(txn.details.value.amount))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación para CREDENCIAL - Solo visible cuando no hay búsqueda global */}
      {searchPerformed && userRole === 'CREDENCIAL' && !globalSearchResults && (
        <div className="pagination-container">
          <button 
            onClick={goToPreviousPage} 
            disabled={page <= 1 || loading}
            className="btn-pagination"
          >
            ◀ Anterior
          </button>
          <span className="pagination-info">
            Página <strong>{page}</strong>
          </span>
          <button 
            onClick={goToNextPage} 
            disabled={transactions.length < 20 || loading}
            className="btn-pagination"
          >
            Siguiente ▶
          </button>
        </div>
      )}

      {/* Exportar para CREDENCIAL - Solo visible cuando no hay búsqueda global */}
      {transactions.length > 0 && userRole === 'CREDENCIAL' && !globalSearchResults && (
        <div className="summary-with-export">
          <button onClick={() => setShowExportModal(true)} className="btn-export-main">
            📥 Exportar
          </button>
        </div>
      )}

      {/* Resumen y Exportar para BIND */}
      {transactions.length > 0 && userRole !== 'CREDENCIAL' && (
        <div className="summary-with-export">
          <div className="summary">
            <p>Movimientos mostrados: <strong>{transactions.length}</strong></p>
            <p>Total débitos: <strong className="text-debit">
              {formatCurrency(
                transactions
                  .filter(t => t.details.type === 'DEBIT')
                  .reduce((sum, t) => sum + Math.abs(t.details.value.amount), 0)
              )}
            </strong></p>
            <p>Total créditos: <strong className="text-credit">
              {formatCurrency(
                transactions
                  .filter(t => t.details.type === 'CREDIT')
                  .reduce((sum, t) => sum + t.details.value.amount, 0)
              )}
            </strong></p>
          </div>
          <div className="export-buttons">
            <button onClick={exportToCSV} className="btn-export btn-csv">
              <LuSheet /> Exportar CSV
            </button>
            <button onClick={exportToPDF} className="btn-export btn-pdf">
              <LuFileText /> Exportar PDF
            </button>
          </div>
        </div>
      )}

      {/* Spinner de carga mientras se consulta el detalle */}
      {detailLoading && (
        <div className="mov-modal-overlay">
          <div className="detail-loading-container">
            <div className="spinner-large"></div>
            <p>Consultando información...</p>
          </div>
        </div>
      )}

      {/* Modal de Exportación */}
      {showExportModal && !showExportConfirm && !isExporting && (
        <div className="export-modal-overlay">
          <div className="export-modal">
            <div className="export-modal-header">
              <h3>Exportar movimientos</h3>
            </div>
            <div className="export-modal-body">
              <div className="export-option-group">
                <label className="export-option-label">Formato:</label>
                <div className="export-options">
                  <label className={`export-option ${exportType === 'csv' ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="exportType" 
                      value="csv"
                      checked={exportType === 'csv'}
                      onChange={() => setExportType('csv')}
                    />
                    <LuSheet /> CSV (Excel)
                  </label>
                  <label className={`export-option ${exportType === 'pdf' ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="exportType" 
                      value="pdf"
                      checked={exportType === 'pdf'}
                      onChange={() => setExportType('pdf')}
                    />
                    <LuFileText /> PDF
                  </label>
                </div>
              </div>
              {/* Solo mostrar opción de scope si NO tiene DB local */}
              {!hasLocalDB && (
              <div className="export-option-group">
                <label className="export-option-label">¿Qué exportar?</label>
                <div className="export-options">
                  <label className={`export-option ${exportScope === 'current' ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="exportScope" 
                      value="current"
                      checked={exportScope === 'current'}
                      onChange={() => setExportScope('current')}
                    />
                    Página actual ({transactions.length} movimientos)
                  </label>
                  <label className={`export-option ${exportScope === 'all' ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="exportScope" 
                      value="all"
                      checked={exportScope === 'all'}
                      onChange={() => setExportScope('all')}
                    />
                    Todas las páginas
                  </label>
                </div>
              </div>
              )}
              
              {/* Mensaje informativo para usuarios con DB local */}
              {hasLocalDB && (
                <div className="export-info-local">
                  <p>📥 Se exportarán <strong>todos los movimientos</strong> del período seleccionado.</p>
                  <p className="export-period">📅 {format(fromDate, 'dd/MM/yyyy')} al {format(toDate, 'dd/MM/yyyy')}</p>
                </div>
              )}
            </div>
            <div className="export-modal-footer">
              <button onClick={() => setShowExportModal(false)} className="btn-cancel">
                Cancelar
              </button>
              <button onClick={handleExportConfirm} className="btn-confirm">
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para exportar TODAS */}
      {showExportModal && showExportConfirm && !isExporting && (
        <div className="export-modal-overlay">
          <div className="export-modal">
            <div className="export-modal-header">
              <h3>Exportar todos los movimientos</h3>
            </div>
            <div className="export-modal-body">
              <div className="export-info">
                <p><strong>📅 Período:</strong> {format(fromDate, 'dd/MM/yyyy')} al {format(toDate, 'dd/MM/yyyy')}</p>
                <p><strong>🏦 Cuenta:</strong> {accountNumber}</p>
              </div>
              <p className="export-warning">
                Se descargarán todos los movimientos del período seleccionado página por página hasta completar.
              </p>
            </div>
            <div className="export-modal-footer">
              <button onClick={() => setShowExportConfirm(false)} className="btn-cancel">
                Volver
              </button>
              <button onClick={startExportAll} className="btn-confirm">
                {exportType === 'csv' ? '📥 Exportar CSV' : '📄 Exportar PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de progreso de exportación */}
      {isExporting && (
        <div className="export-modal-overlay">
          <div className="export-modal">
            <div className="export-modal-header">
              <h3>Exportando...</h3>
            </div>
            <div className="export-modal-body">
              <div className="export-progress">
                {/* Spinner para usuarios con DB local (exportación rápida) */}
                {hasLocalDB ? (
                  <>
                    <div className="export-spinner-container">
                      <LuLoader className="export-spinner" />
                    </div>
                    <p className="progress-text">
                      Preparando exportación...
                    </p>
                    <p className="progress-movements">
                      📅 {format(fromDate, 'dd/MM/yyyy')} al {format(toDate, 'dd/MM/yyyy')}
                    </p>
                  </>
                ) : (
                  <>
                    {/* Progreso tradicional para usuarios sin DB */}
                <div className="progress-bar-container progress-bar-animated">
                  <div className="progress-bar-fill-animated" />
                </div>
                <p className="progress-text">
                  Descargando página <strong>{exportProgress.current}</strong>...
                </p>
                <p className="progress-movements">
                  <strong>{exportProgress.movements}</strong> movimientos recopilados
                </p>
                  </>
                )}
              </div>
            </div>
            {!hasLocalDB && (
            <div className="export-modal-footer">
              <button onClick={cancelExport} className="btn-cancel-export">
                ✕ Cancelar exportación
              </button>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Detalle de Movimiento - CREDENCIAL */}
      {showDetailModal && selectedMovement && !detailLoading && (
        <div className="mov-modal-overlay" onClick={handleCloseDetailModal}>
          <div className="mov-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mov-modal-header">
              <span>Detalle del Movimiento</span>
              <button onClick={handleCloseDetailModal}><LuX /></button>
            </div>
            
            <div className="mov-modal-content">
              <table className="mov-detail-table">
                <tbody>
                  <tr>
                    <td>Fecha</td>
                    <td>{formatTimestamp(selectedMovement.timestamp)}</td>
                  </tr>
                  <tr>
                    <td>Monto</td>
                    <td className={parseFloat(selectedMovement.amount) >= 0 ? 'text-credit' : 'text-debit'}>
                      {formatCurrency(parseFloat(selectedMovement.amount) || 0)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {isCoelsaTransfer(selectedMovement) && (
                <>
                  <div className="mov-section-title">
                    {parseFloat(selectedMovement.amount) >= 0 ? 'Información de la cuenta de origen' : 'Información de la cuenta destino'}
                  </div>
                  <table className="mov-detail-table">
                    <tbody>
                      <tr>
                        <td>CVU</td>
                        <td className="mono">{parseCvuFromMerchantName(selectedMovement.merchant_name) || '-'}</td>
                      </tr>
                      {cvuInfo && (
                        <>
                          <tr>
                            <td>Titular</td>
                            <td>{cvuInfo.nombre || '-'}</td>
                          </tr>
                          <tr>
                            <td>CUIT</td>
                            <td>{cvuInfo.cuit || '-'}</td>
                          </tr>
                          <tr>
                            <td>Alias</td>
                            <td>{cvuInfo.alias || '-'}</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </>
              )}
              
              <div className="mov-modal-actions">
                <button 
                  className="btn-download-comprobante"
                  onClick={handleDownloadComprobante}
                >
                  📥 Descargar Comprobante
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de información de Origen */}
      {showOriginModal && selectedOrigin && (
        <div className="mov-modal-overlay" onClick={handleCloseOriginModal}>
          <div className="mov-modal origin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mov-modal-header">
              <span>Información del Origen</span>
              <button onClick={handleCloseOriginModal}><LuX /></button>
            </div>
            
            <div className="mov-modal-content">
              <table className="mov-detail-table">
                <tbody>
                  <tr>
                    <td>Titular</td>
                    <td><strong>{selectedOrigin.nombre}</strong></td>
                  </tr>
                  <tr>
                    <td>CVU</td>
                    <td className="mono">{selectedOrigin.cvu}</td>
                  </tr>
                  <tr>
                    <td>CUIT</td>
                    <td>{selectedOrigin.cuit}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MovementsPage


